<template>
  <div
    class="map-container"
  >
    <div class="search-container">
      <b-form-input
        ref="search"
        class="search-input"
        type="text"
        v-model="search"
        placeholder="Digite o endereço"
        :readonly="!enableSearch"
        :autocomplete="enableSearch ? 'on' : 'off'"
      />
    </div>
    <div
      ref="map"
      id="map"
      class="map"
    />
    <b-form-checkbox
      class="py-2 mx-1"
      v-model="enableSearch"
    >
      Localização Manual?
    </b-form-checkbox>
  </div>
</template>

<!-- eslint-disable no-undef -->
<script>
import { Loader } from '@googlemaps/js-api-loader'

import { toast } from '@/shared/utils/UIHelper'

const retryAsync = (maxAttempts, expected, action) => {
  return new Promise(async (resolve, reject) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const result = await action()

      if (expected(result)) {
        resolve(result)
        return
      }

      await new Promise(resolve => setTimeout(resolve, 1000))

      if (attempt === maxAttempts) {
        reject(result)
      }
    }
  })
}

export default {
  data () {
    return {
      initialized: false,
      loader: null,
      enableSearch: false,
      search: '',
      currentLocationMarker: null
    }
  },
  watch: {
    enableSearch: function (value) {
      if (value) {
        this.initAutoComplete()
      } else {
        this.initAutoComplete(true)
      }
    }
  },
  mounted () {
    this.requestLocationAccess()
  },
  methods: {
    requestLocationAccess () {
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          this.handleLocationSuccess,
          this.handleLocationError
        )
      } else {
        toast.error('Geolocalização é necessária para essa ação')
      }
    },
    async handleLocationSuccess (position) {
      await this.$nextTick(async () => {
        await this.init(position.coords)
      })
    },
    handleLocationError (error) {
      toast.error('Ative a geolocalização para essa ação')
      switch (error.code) {
        case error.PERMISSION_DENIED:
          console.error('User denied the request for Geolocation.')
          break
        case error.POSITION_UNAVAILABLE:
          console.error('Location information is unavailable.')
          break
        case error.TIMEOUT:
          console.error('The request to get user location timed out.')
          break
        case error.UNKNOWN_ERROR:
          console.error('An unknown error occurred.')
          break
      }
    },
    async init (coords) {
      this.loader = new Loader({
        apiKey: process.env.MapsApiKey,
        version: process.env.MapsApiVersion
      })

      await this.loader.load()
        .then(async _ => {
          await Promise.all([
            this.initMap(coords),
            this.setAutoCompleteAddress(coords)
          ])
        })

      this.$nextTick(() => {
        this.initialized = true
        this.$emit('initialized')
      })
    },
    async initMap (coords) {
      const { Map } = await google.maps.importLibrary('maps')
      const mapRef = this.$refs.map

      this.map = new Map(
        mapRef,
        {
          center: { lat: coords.latitude, lng: coords.longitude },
          zoom: 18
        })

      this.setCurrentLocation(coords)
    },
    async initAutoComplete (disable = false) {
      const places = await google.maps.importLibrary('places')

      const searchInput = this.$refs.search.$refs.input
      const autocomplete = new places.Autocomplete(searchInput)

      const handler = () => this.handlePlaceChange(autocomplete)
      const listener = autocomplete.addListener('place_changed', handler)

      if (disable) {
        google.maps.event.removeListener(listener)
        google.maps.event.clearInstanceListeners(autocomplete)

        const pacContainer = document.querySelector('.pac-container')

        if (pacContainer) {
          pacContainer.parentNode.removeChild(pacContainer)
        }
      }
    },
    handlePlaceChange (autocomplete) {
      const place = autocomplete.getPlace()

      console.log(place)

      this.search = place.formatted_address

      this.setCurrentLocation({
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng()
      })
    },
    setAutoCompleteAddress (coords) {
      const geocoder = new google.maps.Geocoder()
      const latLng = new google.maps.LatLng(coords.latitude, coords.longitude)

      geocoder.geocode({ location: latLng }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK) {
          if (results[0]) {
            const formattedAddress = results[0].formatted_address
            this.search = formattedAddress
          } else {
            console.error('No address found for the given coordinates.')
          }
        } else {
          console.error('Geocoder failed with status: ' + status)
        }
      })
    },
    setCurrentLocation (coords) {
      const currentLocation = new google.maps.LatLng(coords.latitude, coords.longitude)

      if (this.currentLocationMarker) {
        this.currentLocationMarker.setMap(null)
      }

      this.currentLocationMarker = new google.maps.Marker({
        position: currentLocation,
        map: this.map,
        title: 'CurrentLocation'
      })

      this.map.setCenter(currentLocation)
      this.$emit('change:location', coords)
    },
    getCurrentLocation () {
      const currentLocation = this.currentLocationMarker

      if (currentLocation) {
        return {
          latitude: currentLocation.position.lat(),
          longitude: currentLocation.position.lng()
        }
      }

      return null
    },
    getDistance (originCoords, destinationCoords) {
      const origin = new google.maps.LatLng(originCoords.latitude, originCoords.longitude)
      const destination = new google.maps.LatLng(destinationCoords.latitude, destinationCoords.longitude)

      const distanceMatrixService = new google.maps.DistanceMatrixService()

      const expected = (response) => response !== null
      const action = async () => {
        let result = null

        await distanceMatrixService.getDistanceMatrix(
          {
            origins: [origin],
            destinations: [destination],
            travelMode: google.maps.TravelMode.DRIVING,
            unitSystem: google.maps.UnitSystem.METRIC,
            avoidHighways: false,
            avoidTolls: false
          }, (response, status) => {
            const success = status === 'OK' &&
              response.rows &&
              response.rows.length &&
              response.rows[0].elements &&
              response.rows[0].elements.length

            if (success) {
              result = response.rows[0].elements[0]
            } else {
              console.error('Erro ao coletar localização:', response)
              result = null
            }
          })

        return result
      }

      return retryAsync(5, expected, action)
    }
  }
}
</script>

<style scoped>
.map-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.map {
  flex-grow: 1;
  width: 100%;
  height: 100%;
}

.search-container {
  width: 100%;
  height: 35px;
}

.search-input {
  width: 100% !important;
  border-left: none;
  border-right: none;
  border-top: none;
  border-radius: 0;
}
</style>



// WEBPACK FOOTER //
// src/views/private/visitas/components/Map.vue